import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';

// Rendered as "Offage Reasons" in Manage Count and "Removal Reasons" in Manual Adjustment — the
// two share this list. Kept in alphabetical order so the picker reads predictably; add new reasons
// in place rather than appending to the end.
const REMOVAL_REASONS = [
  'Administrative Error',
  'Contaminated',
  'Damaged / Broken',
  'Expired',
  'Inventory Count',
  'Missing',
  'Near Expiry',
  'Recalled',
  'Temperature Excursion',
  'Transfer Out',
];

const RemoveReasonRows = ({ rows, onChange, totalQty, submitted }) => {
  const handleQtyChange = (index, value) => {
    onChange(rows.map((r, i) => (i === index ? { ...r, qty: value } : r)));
  };

  const handleReasonsChange = (index, value) => {
    onChange(rows.map((r, i) => (i === index ? { ...r, reasons: value } : r)));
  };

  const handleAddRow = () => {
    onChange([...rows, { qty: '', reasons: [] }]);
  };

  const handleRemoveRow = (index) => {
    onChange(rows.filter((_, i) => i !== index));
  };

  const totalNum = parseFloat(totalQty) || 0;
  const allocatedSum = rows.reduce((sum, r) => sum + (parseFloat(r.qty) || 0), 0);
  const isOver = allocatedSum > totalNum;
  const isMatch = totalNum > 0 && allocatedSum === totalNum;

  return (
    <Box>
      <Typography variant='caption' color='text.secondary' sx={{ mb: 1, display: 'block' }}>
        Split the quantity across one or more reason groups
      </Typography>

      {rows.map((row, index) => {
        // A row turns red only once the user has put something in it (or the form was submitted).
        // A freshly revealed, empty row is not a mistake yet — flagging it the moment a variance
        // appears makes the screen look broken before anyone has done anything wrong.
        const rowDirty = row.qty !== '' || row.reasons.length > 0;
        const showRowErrors = submitted || rowDirty;
        const otherSum = rows.reduce(
          (s, r, ri) => (ri !== index ? s + (parseFloat(r.qty) || 0) : s),
          0
        );
        const maxForRow = totalNum - otherSum;
        const qtyNum = parseFloat(row.qty) || 0;
        const qtyExceedsMax = String(row.qty).trim() !== '' && totalNum > 0 && qtyNum > maxForRow;
        const qtyInvalid = String(row.qty).trim() !== '' && qtyNum < 1;

        let qtyHelperText;
        if (qtyInvalid) qtyHelperText = 'Min 1';
        else if (qtyExceedsMax)
          qtyHelperText = maxForRow <= 0 ? 'Fully accounted' : `Max ${maxForRow}`;

        return (
          // eslint-disable-next-line react/no-array-index-key
          <Grid container spacing={1} alignItems='flex-start' key={index} sx={{ mb: 1 }}>
            <Grid item xs={3}>
              <TextField
                label='Qty *'
                size='small'
                fullWidth
                type='number'
                inputProps={{ min: 1, step: 1, style: { textAlign: 'right' } }}
                value={row.qty}
                onChange={(e) => handleQtyChange(index, e.target.value)}
                error={
                  (showRowErrors && (!row.qty || parseFloat(row.qty) <= 0)) ||
                  qtyExceedsMax ||
                  qtyInvalid
                }
                helperText={qtyHelperText}
              />
            </Grid>
            <Grid item xs={8}>
              <Autocomplete
                multiple
                freeSolo
                disableCloseOnSelect
                size='small'
                options={REMOVAL_REASONS}
                value={row.reasons}
                onChange={(_, value) => handleReasonsChange(index, value)}
                filterSelectedOptions
                renderTags={(value, getTagProps) =>
                  value.map((option, i) => {
                    const tagProps = getTagProps({ index: i });
                    // eslint-disable-next-line react/no-array-index-key, react/jsx-props-no-spreading
                    return <Chip key={i} label={option} size='small' {...tagProps} />;
                  })
                }
                renderInput={(params) => (
                  <TextField
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...params}
                    label='Reasons *'
                    size='small'
                    error={showRowErrors && row.reasons.length === 0}
                    helperText={
                      showRowErrors && row.reasons.length === 0 ? 'Select at least one reason' : ''
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={1} sx={{ display: 'flex', alignItems: 'center', pt: '6px !important' }}>
              <IconButton
                size='small'
                onClick={() => handleRemoveRow(index)}
                disabled={rows.length === 1}
                sx={{ color: 'error.light' }}
              >
                <DeleteOutlineIcon fontSize='small' />
              </IconButton>
            </Grid>
          </Grid>
        );
      })}

      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.75 }}
      >
        <Button
          size='small'
          variant='outlined'
          startIcon={<AddIcon />}
          onClick={handleAddRow}
          sx={{ textTransform: 'none' }}
        >
          Add reason
        </Button>
        <Typography
          variant='caption'
          sx={{
            fontWeight: isMatch ? 700 : 400,
            // eslint-disable-next-line no-nested-ternary
            color: isOver ? 'error.main' : isMatch ? 'success.main' : 'text.secondary',
          }}
        >
          Allocated: {allocatedSum} of {totalNum}
        </Typography>
      </Box>
    </Box>
  );
};

RemoveReasonRows.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      qty: PropTypes.string,
      reasons: PropTypes.arrayOf(PropTypes.string),
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  totalQty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  submitted: PropTypes.bool,
};
RemoveReasonRows.defaultProps = { totalQty: 0, submitted: false };

export default RemoveReasonRows;
